import { supabase } from "@/integrations/supabase/client";

export interface JobAnalysisData {
  jobTitle: string;
  industry?: string;
  responsibilities: string[];
  requirements: string[];
  skills: string[];
}

export interface SavedJobAnalysis {
  id: string;
  user_id: string;
  job_title: string;
  industry?: string;
  position_display_name: string;
  selected_responsibilities: any[];
  selected_requirements: any[];
  selected_skills: any[];
  all_responsibilities: any[];
  all_requirements: any[];
  all_skills: any[];
  created_at: string;
  updated_at: string;
}

export class JobAnalysisService {
  static async generateJobAnalysis(jobTitle: string, industry?: string): Promise<JobAnalysisData> {
    const { data, error } = await supabase.functions.invoke('generate-job-analysis', {
      body: { jobTitle, industry }
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || 'Failed to generate job analysis');

    return data.analysis;
  }

  static async saveJobAnalysis(data: {
    jobTitle: string;
    industry?: string;
    selectedResponsibilities: string[];
    selectedRequirements: string[];
    selectedSkills: string[];
    allResponsibilities: string[];
    allRequirements: string[];
    allSkills: string[];
  }): Promise<SavedJobAnalysis> {
    const user = await supabase.auth.getUser();
    if (!user.data.user) throw new Error('User not authenticated');

    const displayName = data.industry 
      ? `${data.jobTitle} - ${data.industry} - Advanced Tailoring Details`
      : `${data.jobTitle} - Advanced Tailoring Details`;

    const { data: savedAnalysis, error } = await supabase
      .from('saved_job_analyses')
      .insert({
        user_id: user.data.user.id,
        job_title: data.jobTitle,
        industry: data.industry,
        position_display_name: displayName,
        selected_responsibilities: data.selectedResponsibilities,
        selected_requirements: data.selectedRequirements,
        selected_skills: data.selectedSkills,
        all_responsibilities: data.allResponsibilities,
        all_requirements: data.allRequirements,
        all_skills: data.allSkills
      })
      .select()
      .single();

    if (error) throw error;
    return savedAnalysis as SavedJobAnalysis;
  }

  static async getSavedJobAnalyses(): Promise<SavedJobAnalysis[]> {
    const user = await supabase.auth.getUser();
    if (!user.data.user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('saved_job_analyses')
      .select('*')
      .eq('user_id', user.data.user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as SavedJobAnalysis[];
  }

  static async getJobAnalysisById(id: string): Promise<SavedJobAnalysis | null> {
    const { data, error } = await supabase
      .from('saved_job_analyses')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return null;
    return data as SavedJobAnalysis;
  }

  static async deleteJobAnalysis(id: string): Promise<void> {
    const { error } = await supabase
      .from('saved_job_analyses')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}